import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiErrorDetail } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";
import {
  Inbox,
  Phone,
  Trash2,
  CheckCircle2,
  Archive,
  RotateCcw,
  Loader2,
  Bell,
  BellOff,
  Smartphone,
  Send,
} from "lucide-react";
import {
  isPushSupported,
  isIos,
  isIosStandalone,
  isCurrentlySubscribed,
  subscribeAdminPush,
  unsubscribeAdminPush,
  sendTestPush,
} from "@/lib/push";
import { logger } from "@/lib/logger";

const STATUS_TABS = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "scheduled", label: "Scheduled" },
  { value: "archived", label: "Archived" },
];

const STATUS_STYLES = {
  new: "bg-blue-100 text-blue-700 border-blue-200",
  contacted: "bg-brand-100 text-brand-700 border-brand-100",
  scheduled: "bg-brand-100 text-brand-800 border-brand-100",
  archived: "bg-stone-200 text-stone-600 border-stone-300",
};

function formatPhone(p) {
  if (!p) return "";
  const d = p.replace(/\D/g, "");
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d.startsWith("1"))
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return p;
}

function formatRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;
  return d.toLocaleDateString();
}

export default function AdminQuotesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);
  const knownIdsRef = useRef(new Set());

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await axios.get(`${API}/quote-requests`, { withCredentials: true });
      setItems((prev) => {
        // detect new arrivals (after first load) and show a toast
        if (knownIdsRef.current.size > 0) {
          const fresh = data.filter((q) => !knownIdsRef.current.has(q.id));
          if (fresh.length > 0) {
            toast.success(`${fresh.length} new quote${fresh.length > 1 ? "s" : ""} arrived`);
          }
        }
        knownIdsRef.current = new Set(data.map((q) => q.id));
        return data;
      });
    } catch (err) {
      if (!silent) toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load quotes");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 20000);
    return () => clearInterval(t);
  }, [load]);

  const counts = useMemo(() => {
    const c = { all: items.length, new: 0, contacted: 0, scheduled: 0, archived: 0 };
    items.forEach((q) => { c[q.status] = (c[q.status] || 0) + 1; });
    return c;
  }, [items]);

  const filtered = filter === "all" ? items : items.filter((q) => q.status === filter);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      const { data } = await axios.patch(`${API}/quote-requests/${id}`, { status }, { withCredentials: true });
      setItems((prev) => prev.map((q) => (q.id === id ? data : q)));
      toast.success(`Marked ${status}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this quote request?")) return;
    setBusyId(id);
    try {
      await axios.delete(`${API}/quote-requests/${id}`, { withCredentials: true });
      setItems((prev) => prev.filter((q) => q.id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error("Failed to delete");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <DashboardShell>
      <div className="mb-8" data-testid="admin-quotes-page">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">Admin</div>
        <h1 className="mt-2 text-4xl font-extrabold text-stone-900" style={{ fontFamily: "Outfit" }}>
          Quote requests
        </h1>
        <p className="mt-2 text-stone-600">
          Every phone number submitted from the landing page lands here. Give them a call back, then mark contacted.
        </p>
      </div>

      <NotificationsPanel />

      <div className="mb-6 flex flex-wrap gap-2" data-testid="admin-status-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setFilter(t.value)}
            data-testid={`tab-${t.value}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              filter === t.value
                ? "bg-stone-900 text-white shadow-md"
                : "bg-white text-stone-600 border border-stone-200 hover:border-stone-300"
            }`}
          >
            {t.label}
            <span
              className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                filter === t.value ? "bg-white/20" : "bg-stone-100"
              }`}
            >
              {counts[t.value] || 0}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 flex justify-center">
          <Loader2 className="w-10 h-10 animate-spin text-brand-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center bg-white rounded-2xl border border-dashed border-stone-300" data-testid="admin-empty-state">
          <div className="w-16 h-16 mx-auto bg-brand-100 rounded-full flex items-center justify-center">
            <Inbox className="w-8 h-8 text-brand-700" />
          </div>
          <h3 className="mt-6 text-2xl font-bold text-stone-900" style={{ fontFamily: "Outfit" }}>
            No quote requests {filter !== "all" ? `in "${filter}"` : "yet"}
          </h3>
          <p className="mt-2 text-stone-600">When a visitor submits the form, you'll see them here.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-quotes-list">
          {filtered.map((q, i) => (
            <div
              key={q.id}
              data-testid={`quote-row-${i}`}
              className="group bg-white border border-stone-200 hover:border-stone-300 hover:shadow-md rounded-2xl p-5 md:p-6 transition-all"
            >
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                <div className="flex items-start gap-4 min-w-0 flex-1">
                  <div className="w-12 h-12 rounded-2xl bg-brand-100 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-brand-700" />
                  </div>
                  <div className="min-w-0 flex-1">
                    {q.name ? (
                      <div className="text-base font-bold text-stone-900 mb-1" data-testid={`quote-name-${i}`}>
                        {q.name}
                      </div>
                    ) : null}
                    <a
                      href={`tel:${q.phone.replace(/\D/g, "")}`}
                      data-testid={`quote-phone-${i}`}
                      className={`${q.name ? "text-lg" : "text-xl md:text-2xl"} font-extrabold text-stone-900 hover:text-brand-700 transition-colors block truncate`}
                      style={{ fontFamily: "Outfit" }}
                    >
                      {formatPhone(q.phone)}
                    </a>
                    <div className="text-sm text-stone-500 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span>{formatRelative(q.created_at)}</span>
                      <span className="text-stone-300">·</span>
                      <span className="truncate">{q.service || "Pressure washing"}</span>
                    </div>
                    {q.address ? (
                      <div
                        data-testid={`quote-address-${i}`}
                        className="mt-2 text-sm text-stone-700 flex items-start gap-1.5"
                      >
                        <svg className="w-4 h-4 text-brand-700 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                        <span>{q.address}</span>
                      </div>
                    ) : null}
                    {q.email ? (
                      <a
                        href={`mailto:${q.email}`}
                        data-testid={`quote-email-${i}`}
                        className="mt-1.5 text-sm text-stone-600 hover:text-brand-700 flex items-center gap-1.5"
                      >
                        <svg className="w-4 h-4 text-brand-700 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <rect x="3" y="5" width="18" height="14" rx="2" />
                          <path d="M3 7l9 6 9-6" />
                        </svg>
                        <span className="truncate">{q.email}</span>
                      </a>
                    ) : null}
                    {q.message ? (
                      <p
                        data-testid={`quote-message-${i}`}
                        className="mt-3 text-sm text-stone-700 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 leading-relaxed"
                      >
                        {q.message}
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <span
                    data-testid={`quote-status-${i}`}
                    className={`text-[10px] font-bold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border ${
                      STATUS_STYLES[q.status] || STATUS_STYLES.new
                    }`}
                  >
                    {q.status}
                  </span>

                  {q.status !== "contacted" && (
                    <button
                      onClick={() => setStatus(q.id, "contacted")}
                      disabled={busyId === q.id}
                      data-testid={`mark-contacted-${i}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-brand-50 hover:bg-brand-100 text-brand-800 border border-brand-100 transition-colors disabled:opacity-50"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Mark contacted
                    </button>
                  )}
                  {q.status !== "archived" ? (
                    <button
                      onClick={() => setStatus(q.id, "archived")}
                      disabled={busyId === q.id}
                      data-testid={`archive-${i}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-stone-50 hover:bg-stone-100 text-stone-700 border border-stone-200 transition-colors disabled:opacity-50"
                    >
                      <Archive className="w-3.5 h-3.5" />
                      Archive
                    </button>
                  ) : (
                    <button
                      onClick={() => setStatus(q.id, "new")}
                      disabled={busyId === q.id}
                      data-testid={`restore-${i}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-stone-50 hover:bg-stone-100 text-stone-700 border border-stone-200 transition-colors disabled:opacity-50"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      Restore
                    </button>
                  )}
                  <button
                    onClick={() => remove(q.id)}
                    disabled={busyId === q.id}
                    data-testid={`delete-${i}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}

function NotificationsPanel() {
  const [supported] = useState(isPushSupported());
  const [ios] = useState(isIos());
  const [standalone, setStandalone] = useState(isIosStandalone());
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setSubscribed(await isCurrentlySubscribed());
      } catch (e) {
        logger.warn("Could not read push subscription state:", e);
      }
    })();
    const onVis = () => setStandalone(isIosStandalone());
    window.addEventListener("visibilitychange", onVis);
    return () => window.removeEventListener("visibilitychange", onVis);
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeAdminPush();
      setSubscribed(true);
      toast.success("Push notifications enabled");
    } catch (err) {
      toast.error(err.message || "Could not enable notifications");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribeAdminPush();
      setSubscribed(false);
      toast.success("Push notifications disabled");
    } catch (e) {
      toast.error("Failed to disable");
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    try {
      await sendTestPush();
      toast.success("Test notification sent");
    } catch (e) {
      toast.error("Test failed");
    } finally {
      setBusy(false);
    }
  };

  const needsIosInstall = ios && !standalone;

  return (
    <div
      data-testid="notifications-panel"
      className="mb-8 rounded-3xl border border-brand-100 bg-gradient-to-br from-brand-50 to-white p-6 md:p-7"
    >
      <div className="flex flex-col md:flex-row md:items-center gap-5">
        <div className="w-14 h-14 rounded-2xl bg-brand-600 text-white flex items-center justify-center flex-shrink-0 shadow-md">
          {subscribed ? <Bell className="w-6 h-6" /> : <Smartphone className="w-6 h-6" />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-700">
            Home-screen widget &amp; push alerts
          </div>
          <div className="mt-1 text-lg font-bold text-stone-900" style={{ fontFamily: "Outfit" }}>
            {subscribed
              ? "Push notifications are on"
              : needsIosInstall
              ? "Install on your iPhone home screen"
              : "Get a ping the moment a quote comes in"}
          </div>
          <p className="mt-1 text-sm text-stone-600 leading-relaxed">
            {needsIosInstall ? (
              <>
                On iPhone, tap the <strong>Share</strong> button in Safari and choose{" "}
                <strong>Add to Home Screen</strong>. Then open the Wynridge app from your home
                screen and enable notifications here.
              </>
            ) : subscribed ? (
              "You'll be notified on your device whenever a customer submits a new quote request."
            ) : !supported ? (
              "Your browser doesn't support push notifications. Try Safari on iPhone (after installing to home screen) or Chrome on Android."
            ) : (
              "Enable browser push notifications to receive an instant alert on your phone or computer whenever someone submits the quote form."
            )}
          </p>
        </div>

        <div className="flex flex-wrap gap-2 md:flex-shrink-0">
          {supported && !needsIosInstall && !subscribed && (
            <button
              onClick={enable}
              disabled={busy}
              data-testid="enable-push-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold bg-brand-600 hover:bg-brand-700 text-white transition-all hover:-translate-y-0.5 shadow-md disabled:opacity-60"
            >
              <Bell className="w-4 h-4" />
              Enable notifications
            </button>
          )}
          {subscribed && (
            <>
              <button
                onClick={test}
                disabled={busy}
                data-testid="test-push-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold bg-white hover:bg-stone-50 text-stone-800 border border-stone-200 transition-all disabled:opacity-60"
              >
                <Send className="w-4 h-4" />
                Send test
              </button>
              <button
                onClick={disable}
                disabled={busy}
                data-testid="disable-push-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold bg-stone-900 hover:bg-stone-800 text-white transition-all disabled:opacity-60"
              >
                <BellOff className="w-4 h-4" />
                Turn off
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

